import React, {Fragment, useEffect, useState} from "react";
import {connect} from "react-redux";
import {Dispatch} from "redux";
import {
    Button,
    Grid,
    LinearProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    useTheme
} from "@mui/material";
import {Delete, Edit, Videocam} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import classNames from "classnames";
import styles from "./ListEventsPage-jss";
import {withStyles} from "@mui/styles";
import {useNavigate} from "react-router-dom";
import Page from "../../components/Page/Page";
import StreamingOptionsDialog from "./components/StreamingOptionsDialog";
import {Profile} from "../User/userDTO";
import {LiveEventsMap, EventsUIState, DeleteEventRequest} from "./EventsDTO";
import {fetchEventsRequest, deleteEventRequest} from "./eventsAtcions";

type Properties = {
    classes: any,
    profile: Profile,
    eventsUI: EventsUIState,
    events: LiveEventsMap,
    fetchEventsRequest: Function,
    deleteEventRequest: Function,
}

function ListEventsPage(props: Properties) {
    const {
        classes,
        profile,
        eventsUI,
        events,
        fetchEventsRequest,
        deleteEventRequest,
    } = props;
    const navigate = useNavigate();
    const [isLoading, setLoading] = useState(true);
    const [selectedEvent, setSelectedEvent] = useState<any>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const theme = useTheme();

    const { t } = useTranslation();

    useEffect(() => {
        if (profile == null || profile['token'] === undefined) {
            navigate("/");
            return;
        }
        fetchEventsRequest(profile.token);
    }, []);

    useEffect(() => {
        setLoading(eventsUI.isLoading);
    }, [eventsUI.isLoading]);

    const handleDelete = async (code: string) => {
        console.log('handleDelete()', code);
        deleteEventRequest({
            token: profile.token,
            slug: events[code].slug
        });
    }

    const handleEnterLiveStudio = (event: any) => {
        setSelectedEvent(event);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedEvent(null);
    };

    const handleChooseRTMP = () => {
        if (selectedEvent) {
            setOpenDialog(false);
            navigate('/studio/' + selectedEvent.code);
        }
    };

    const handleChooseLiveStudio = () => {
        if (selectedEvent) {
            setOpenDialog(false);
            // Navigate directly to the simplified LiveStudio page with the event code as roomId
            navigate(`/live-studio/${selectedEvent.code}`);
        }
    };

    return (
        <Page desc="" title={t('Events')}>
            <Grid container direction="column" spacing={5} alignItems="flex-start">
                <Grid item>
                    <Button
                        variant="contained"
                        color="primary"
                        className={classes.newEventButton}
                        onClick={() => { navigate('/events/new') }}
                        sx={{
                            fontWeight: 600,
                            textTransform: 'uppercase',
                        }}
                    >
                        {t('Create an Event')}
                    </Button>
                </Grid>

                <Grid item container
                      direction="row"
                      alignItems="flex-start"
                      xs={10}
                      md={10}
                      lg={10}>
                    {isLoading ? (
                        <div className={classNames(classes.loaderArea)}>
                            <LinearProgress />
                        </div>
                    ): (
                        <Fragment>
                            {Object.keys(events).length > 0 ? (
                                <TableContainer
                                    sx={{
                                        backgroundColor: theme.palette.background.default,
                                        borderRadius: '8px',
                                    }}
                                >
                                    <Table
                                        className={classNames(classes.table)}
                                        aria-label="events table"
                                        sx={{
                                            '& .MuiTableHead-root': {
                                                '& .MuiTableCell-root': {
                                                    color: theme.palette.text.primary,
                                                    fontWeight: 600,
                                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                                    padding: '16px',
                                                },
                                            },
                                            '& .MuiTableBody-root': {
                                                '& .MuiTableRow-root': {
                                                    '&:hover': {
                                                        backgroundColor: theme.palette.background.paper,
                                                    },
                                                    '& .MuiTableCell-root': {
                                                        color: theme.palette.text.primary,
                                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                                        padding: '16px',
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell width="30%">{t('Event')}</TableCell>
                                                {/*<TableCell>{t('Created at')}</TableCell>*/}
                                                <TableCell width="60%"></TableCell>
                                                <TableCell width="5%"></TableCell>
                                                <TableCell width="5%"></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.keys(events).map(code => (
                                                <TableRow key={events[code].code}>
                                                    <TableCell sx={{ color: theme.palette.text.primary }}>
                                                        {events[code].title}
                                                    </TableCell>
                                                    {/*<TableCell>{new Date(events[code].created_at).toLocaleDateString()}</TableCell>*/}
                                                    <TableCell>
                                                        <Button
                                                            variant="contained"
                                                            disableElevation
                                                            onClick={() => handleEnterLiveStudio(events[code])}
                                                            startIcon={<Videocam />}
                                                            sx={{
                                                                backgroundColor: '#2c3e50',
                                                                color: '#ffffff',
                                                                fontWeight: 600,
                                                                textTransform: 'uppercase',
                                                                borderRadius: '8px',
                                                                padding: '10px 20px',
                                                                '&:hover': {
                                                                    backgroundColor: '#34495e',
                                                                },
                                                                '& .MuiSvgIcon-root': {
                                                                    color: '#ffffff',
                                                                },
                                                            }}
                                                        >
                                                            {t('Enter Live Studio')}
                                                        </Button>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title={t('Edit')||''}>
                                                            <Button
                                                                onClick={() => navigate('/events/' + events[code].code)}
                                                                startIcon={<Edit />}
                                                                aria-label={t('Edit')}
                                                                sx={{
                                                                    color: theme.palette.text.primary,
                                                                    textTransform: 'uppercase',
                                                                    fontWeight: 500,
                                                                    '&:hover': {
                                                                        backgroundColor: 'transparent',
                                                                        color: theme.palette.text.primary,
                                                                    },
                                                                    '& .MuiSvgIcon-root': {
                                                                        color: theme.palette.text.primary,
                                                                    },
                                                                }}
                                                            >
                                                                {t('Edit')}
                                                            </Button>
                                                        </Tooltip>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Tooltip title={t("Delete")||''}>
                                                            <Button
                                                                onClick={() => handleDelete(events[code].code)}
                                                                startIcon={<Delete />}
                                                                aria-label={t('Delete')}
                                                                sx={{
                                                                    color: theme.palette.text.primary,
                                                                    textTransform: 'uppercase',
                                                                    fontWeight: 500,
                                                                    '&:hover': {
                                                                        backgroundColor: 'transparent',
                                                                        color: theme.palette.text.primary,
                                                                    },
                                                                    '& .MuiSvgIcon-root': {
                                                                        color: theme.palette.text.primary,
                                                                    },
                                                                }}
                                                            >
                                                                {t('Delete')}
                                                            </Button>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            ) : <div className={classNames(classes.noEventDiv)} style={{ color: theme.palette.text.secondary }}>
                                {t('You still haven\'t created any event')}
                            </div>}
                        </Fragment>
                    )}
                </Grid>
            </Grid>

            {/* Streaming Options Dialog */}
            <StreamingOptionsDialog
                open={openDialog}
                onClose={handleCloseDialog}
                onChooseRTMP={handleChooseRTMP}
                onChooseLiveStudio={handleChooseLiveStudio}
                eventTitle={selectedEvent?.title}
                eventDescription={selectedEvent?.details}
            />
        </Page>
    );
}

const mapStateToProps = function (state: any) {
    return {
        isLoggedIn: state.auth.isLoggedIn,
        profile: state.user.profile,
        events: state.events,
        eventsUI: state.eventsUI,
    };
};

function mapDispatchToProps(dispatch: Dispatch) {
    return {
        fetchEventsRequest: (token: string) => dispatch(fetchEventsRequest(token)),
        deleteEventRequest: (request: DeleteEventRequest) => dispatch(deleteEventRequest(request)),
    };
}

export default withStyles(styles)(connect(mapStateToProps, mapDispatchToProps)(ListEventsPage));
